import { supabase } from '@/lib/supabase';

/**
 * FIFO reallocation: for a set of SKUs, walk reservation_components in
 * reservation creation order and move expected→allocated as physical stock
 * becomes available, or allocated→expected when stock has decreased.
 *
 * Also reconciles the component-level reserved_qty / expected_qty / available_qty
 * to match the updated per-row totals.
 *
 * Call this any time total_qty or on_order_qty changes for a component —
 * whether via BC import, manual adjustment, or any other path.
 */
export async function fifoReallocate(skus: string[]): Promise<void> {
  if (skus.length === 0) return;

  // Active reservations in FIFO order
  const { data: activeRes } = await supabase
    .from('reservations')
    .select('id, created_at')
    .not('status', 'in', '("Completed","Cancelled")')
    .order('created_at', { ascending: true });
  if (!activeRes?.length) return;

  // reservation_components for these SKUs across all active reservations
  const activeIds = activeRes.map(r => r.id);
  const allRc: any[] = [];
  for (let i = 0; i < activeIds.length; i += 100) {
    const { data } = await supabase
      .from('reservation_components')
      .select('id, reservation_id, component_sku, quantity, allocated_qty, expected_qty')
      .in('reservation_id', activeIds.slice(i, i + 100))
      .in('component_sku', skus);
    allRc.push(...(data || []));
  }

  // Current stock for these SKUs
  const { data: stockRows } = await supabase
    .from('components')
    .select('part_number, total_qty, reserved_qty, expected_qty')
    .in('part_number', skus);
  const stockMap: Record<string, any> = {};
  (stockRows || []).forEach((s: any) => { stockMap[s.part_number] = s; });

  for (const sku of skus) {
    const stock = stockMap[sku];
    if (!stock) continue;

    let physicalRemaining = stock.total_qty ?? 0;

    const rows = allRc
      .filter(rc => rc.component_sku === sku)
      .sort((a, b) => {
        const resA = activeRes.find(r => r.id === a.reservation_id);
        const resB = activeRes.find(r => r.id === b.reservation_id);
        return new Date(resA!.created_at).getTime() - new Date(resB!.created_at).getTime();
      });

    let newTotalReserved = 0;
    let newTotalExpected = 0;

    for (const rc of rows) {
      const totalNeed = (rc.allocated_qty ?? 0) + (rc.expected_qty ?? 0);
      const newAllocated = Math.min(totalNeed, physicalRemaining);
      const newExpected = totalNeed - newAllocated;
      physicalRemaining -= newAllocated;
      newTotalReserved += newAllocated;
      newTotalExpected += newExpected;

      if (newAllocated !== (rc.allocated_qty ?? 0) || newExpected !== (rc.expected_qty ?? 0)) {
        await supabase.from('reservation_components').update({
          allocated_qty: newAllocated,
          expected_qty: newExpected,
        }).eq('id', rc.id);
      }
    }

    const newAvailable = Math.max(0, (stock.total_qty ?? 0) - newTotalReserved);
    if (stock.reserved_qty !== newTotalReserved || stock.expected_qty !== newTotalExpected) {
      await supabase.from('components').update({
        available_qty: newAvailable,
        reserved_qty: newTotalReserved,
        expected_qty: newTotalExpected,
      }).eq('part_number', sku);
    }
  }
}
